import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Filter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TaskFilters, TaskStatus } from "../../types/tasks";
import { TASK_STATUSES } from "../../constants/taskConstants";

interface Partner  { _id: string; companyName: string }
interface Assignee { _id: string; firstName: string; lastName: string }

interface Props {
  filters: TaskFilters;
  onChange: (filters: Partial<TaskFilters>) => void;
  onClear: () => void;
  activeCount: number;
  partners?: Partner[];
  assignees?: Assignee[];
}

const FilterSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  badge?: number;
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-2.5 text-left group"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-dark-700 uppercase tracking-wide">
            {title}
          </span>
          {badge !== undefined && badge > 0 && (
            <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary text-white rounded-full">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 text-dark-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
};

export const TaskListFilters: React.FC<Props> = ({
  filters,
  onChange,
  onClear,
  activeCount,
  partners = [],
  assignees = [],
}) => {
  const { t } = useTranslation("tasks");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleFromArray = <T extends string>(
    arr: T[] | undefined,
    value: T,
  ): T[] => {
    const list = arr ?? [];
    return list.includes(value)
      ? list.filter((v) => v !== value)
      : [...list, value];
  };

  const statusCount = filters.status?.length ?? 0;
  const dateCount = (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0);
  const optionsCount =
    (filters.hasStructuredForm ? 1 : 0) + (filters.overdueOnly ? 1 : 0);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center space-x-2 px-4 py-2 border border-dark-300 rounded-lg hover:bg-dark-50 transition-colors bg-white"
      >
        <Filter className="h-4 w-4" />
        <span>{t("list.filters.title")}</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-primary text-white rounded-full">
            {activeCount}
          </span>
        )}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-md:w-72 bg-white rounded-xl shadow-xl border border-dark-200 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm font-semibold text-dark-800">
              {t("list.filters.title")}
            </span>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={onClear}
                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                >
                  {t("list.filters.clearAll")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-dark-400 hover:text-dark-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-4 py-1 space-y-0.5 scrollbar-thin">
            <FilterSection
              title={t("list.filters.dateRange")}
              defaultOpen={dateCount > 0}
              badge={dateCount}
            >
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-dark-500 mb-1">
                    {t("list.filters.dateFrom")}
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom ?? ""}
                    onChange={(e) =>
                      onChange({ dateFrom: e.target.value || undefined })
                    }
                    className="w-full px-2 py-1.5 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs text-dark-500 mb-1">
                    {t("list.filters.dateTo")}
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo ?? ""}
                    onChange={(e) =>
                      onChange({ dateTo: e.target.value || undefined })
                    }
                    className="w-full px-2 py-1.5 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                  />
                </div>
              </div>
            </FilterSection>

            <FilterSection
              title={t("list.filters.status")}
              defaultOpen={statusCount > 0}
              badge={statusCount}
            >
              <div className="grid grid-cols-2 gap-1.5">
                {TASK_STATUSES.map((s) => (
                  <label
                    key={s}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={filters.status?.includes(s) ?? false}
                      onChange={() =>
                        onChange({
                          status: toggleFromArray<TaskStatus>(
                            filters.status,
                            s,
                          ),
                        })
                      }
                      className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                    />
                    <span className="text-xs text-dark-600 leading-none">
                      {t(`form.statusOptions.${s}`)}
                    </span>
                  </label>
                ))}
              </div>
            </FilterSection>

            <FilterSection
              title={t("list.filters.client")}
              defaultOpen={(filters.clientIds?.length ?? 0) > 0}
              badge={filters.clientIds?.length ?? 0}
            >
              <select
                className="w-full px-2 py-1.5 border border-dark-300 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                value={filters.clientIds?.[0] ?? ""}
                onChange={(e) =>
                  onChange({
                    clientIds: e.target.value ? [e.target.value] : [],
                  })
                }
              >
                <option value="">—</option>
                {partners.map((p) => (
                  <option key={p._id} value={p._id}>{p.companyName}</option>
                ))}
              </select>
            </FilterSection>

            <FilterSection
              title={t("list.filters.assignedTo")}
              defaultOpen={(filters.assigneeIds?.length ?? 0) > 0}
              badge={filters.assigneeIds?.length ?? 0}
            >
              <select
                className="w-full px-2 py-1.5 border border-dark-300 rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-transparent text-xs"
                value={filters.assigneeIds?.[0] ?? ""}
                onChange={(e) =>
                  onChange({
                    assigneeIds: e.target.value ? [e.target.value] : [],
                  })
                }
              >
                <option value="">—</option>
                {assignees.map((u) => (
                  <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
            </FilterSection>

            <FilterSection
              title={t("list.filters.title")}
              defaultOpen={optionsCount > 0}
              badge={optionsCount}
            >
              <div className="space-y-1">
                <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.hasStructuredForm ?? false}
                    onChange={(e) =>
                      onChange({
                        hasStructuredForm: e.target.checked ? true : undefined,
                      })
                    }
                    className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                  />
                  <span className="text-xs text-dark-600 leading-none">
                    {t("list.filters.hasStructuredForm")}
                  </span>
                </label>
                <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.overdueOnly ?? false}
                    onChange={(e) =>
                      onChange({
                        overdueOnly: e.target.checked ? true : undefined,
                      })
                    }
                    className="rounded border-dark-300 text-primary focus:ring-primary w-3.5 h-3.5 shrink-0"
                  />
                  <span className="text-xs text-dark-600 leading-none">
                    {t("list.filters.overdueOnly")}
                  </span>
                </label>
              </div>
            </FilterSection>
          </div>

          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full text-sm bg-primary text-white py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              {t("list.filters.applyFilters")}
              {activeCount > 0 && ` (${activeCount})`}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
